import { defineStore } from "pinia";
import { buildQueryset } from "@/utils/queryset";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { useProjectsStore } from "@/store/modules/projects";
import { summarizeAcquisition } from "@/utils/acquisitionContent";
import { sortByOrdering } from "@/utils/helpers";
import { acquisitionBookRestClient } from "@/api/clients/acquisitionRecordBooksClient";
import { OfflineError } from "@/utils/errors";
import { isNetworkUnavailable } from "@/composables/network/offlineMode";

const { innerCreate, innerUpdate, innerFetch, innerDelete } = crudGenerator(
  db.models.AcquisitionRecordBook
);

const DEFAULT_ORDERING = "analyserDate";

const assertOnline = () => {
  if (useProjectsStore().isOfflineState || isNetworkUnavailable())
    throw new OfflineError();
};

const draftRank = ({ isDraft }) => (isDraft ? 0 : 1);

const storeComputed = async (data) => {
  await db.models.AcquisitionRecordBook.createOrUpdate(data);
  return db.acquisitionRecordBooks.get({ uuid: data.uuid });
};

const instantsOf = (trials, key) =>
  trials.map((trial) => Date.parse(trial[key])).filter(Number.isFinite);

const trialRangeOf = ({ trialsHours }) => {
  const trials = Object.values(trialsHours ?? {});
  const starts = instantsOf(trials, "startAt");
  const ends = instantsOf(trials, "endAt");
  return {
    start: starts.length ? Math.min(...starts) : null,
    end: ends.length ? Math.max(...ends) : null,
  };
};

const toListItem = (acquisitionRecordBook) => {
  const content = summarizeAcquisition(acquisitionRecordBook);
  acquisitionRecordBook.content = acquisitionRecordBook.isDraft
    ? { ...content, ...trialRangeOf(acquisitionRecordBook) }
    : content;
  delete acquisitionRecordBook.graphsPoint;
  return acquisitionRecordBook;
};

const uniqueValues = (values) => [...new Set(values.filter(Boolean))];

const analyserInfoByUuid = async (acquisitionRecordBooks) => {
  const analysers = (
    await db.recordBooks.bulkGet(
      uniqueValues(
        acquisitionRecordBooks.flatMap(({ analyserBooks }) => analyserBooks)
      )
    )
  ).filter(Boolean);
  const environments = (
    await db.recordBooks.bulkGet(
      uniqueValues(
        analysers.map(({ environmentalCondition }) => environmentalCondition)
      )
    )
  ).filter(Boolean);
  const dateByEnvironment = new Map(
    environments.map(({ uuid, recordForm }) => [
      uuid,
      recordForm?.interventionDate ?? null,
    ])
  );
  return new Map(
    analysers.map(({ uuid, label, environmentalCondition }) => [
      uuid,
      { uuid, label, date: dateByEnvironment.get(environmentalCondition) },
    ])
  );
};

const withAnalysers = async (acquisitionRecordBooks) => {
  const infoByUuid = await analyserInfoByUuid(acquisitionRecordBooks);
  for (const acquisitionRecordBook of acquisitionRecordBooks) {
    const analysers = (acquisitionRecordBook.analyserBooks ?? [])
      .map((uuid) => infoByUuid.get(uuid))
      .filter(Boolean);
    acquisitionRecordBook.analysers = analysers;
    acquisitionRecordBook.analyserDate =
      analysers
        .map(({ date }) => date)
        .filter(Boolean)
        .toSorted()[0] ?? null;
  }
  return acquisitionRecordBooks;
};

const pageOf = (items, { page, page_size: pageSize }) => {
  if (!pageSize || pageSize < 0) return items;
  const offset = ((page || 1) - 1) * pageSize;
  return items.slice(offset, offset + pageSize);
};

export const useAcquisitionRecordBooksStore = defineStore(
  "acquisitionRecordBooks",
  {
    state: () => ({
      acquisitionRecordBookList: [],
      acquisitionIsLoading: "",
      acquisitionIsFetching: false,
      acquisitionIsSyncing: false,
      totalAcquisitionCount: 0,
    }),
    actions: {
      async createAcquisitionRecordBook(form) {
        assertOnline();
        this.acquisitionIsFetching = true;
        try {
          return await storeComputed(
            await acquisitionBookRestClient.create(form)
          );
        } finally {
          this.acquisitionIsFetching = false;
        }
      },

      async completeAcquisitionDraft(uuid, form) {
        assertOnline();
        this.acquisitionIsFetching = true;
        try {
          return await storeComputed(
            await acquisitionBookRestClient.complete(uuid, form)
          );
        } finally {
          this.acquisitionIsFetching = false;
        }
      },

      async saveAcquisitionDraft(form) {
        const { isOfflineState } = useProjectsStore();
        const draft = { ...form, isDraft: true };
        return form.uuid
          ? innerUpdate(draft, isOfflineState)
          : innerCreate(draft, isOfflineState);
      },

      async updateAcquisitionRecordBook(form) {
        this.acquisitionIsFetching = true;
        this.acquisitionIsLoading = true;

        try {
          const acquisitionRecordBook = await innerUpdate(
            form,
            useProjectsStore().isOfflineState
          );

          return acquisitionRecordBook;
        } finally {
          this.acquisitionIsFetching = false;
          this.acquisitionIsLoading = false;
        }
      },

      async getAcquisitionRecordBook(uuid) {
        return db.acquisitionRecordBooks.get({ uuid });
      },

      async fetchAcquisitionRecordBook(projectUuid) {
        return innerFetch(
          { project: projectUuid },
          useProjectsStore().isOfflineState,
          (isSyncing) => {
            this.acquisitionIsSyncing = isSyncing;
          }
        );
      },

      async deleteAcquisitionRecordBook(uuid) {
        const projectsStore = useProjectsStore();
        await innerDelete(
          uuid,
          projectsStore.isOfflineState,
          projectsStore.currentProjectUuid
        );
        this.acquisitionRecordBookList = this.acquisitionRecordBookList.filter(
          (recordBook) => recordBook.uuid !== uuid
        );
        this.totalAcquisitionCount -= 1;
      },

      async filterAcquisitionRecordBooks({
        queryAsObject,
        ordering,
        filterMapping,
      }) {
        while (this.acquisitionIsFetching)
          await new Promise((r) => setTimeout(r, 200));
        const showSkeleton = !this.acquisitionRecordBookList.length;
        this.acquisitionIsFetching = true;
        if (showSkeleton) this.acquisitionIsLoading = true;
        try {
          const { queryset } = await buildQueryset(
            db.acquisitionRecordBooks,
            queryAsObject,
            "",
            filterMapping,
            true
          );
          const acquisitionRecordBooks = await withAnalysers(
            await queryset.toArray()
          );
          const sorted = sortByOrdering(
            acquisitionRecordBooks,
            `${ordering || DEFAULT_ORDERING},label`
          ).toSorted((a, b) => draftRank(a) - draftRank(b));
          this.acquisitionRecordBookList = pageOf(sorted, queryAsObject).map(
            toListItem
          );
          this.totalAcquisitionCount = sorted.length;
        } finally {
          this.acquisitionIsFetching = false;
          if (showSkeleton) this.acquisitionIsLoading = false;
        }
      },
    },
  }
);
